import type { DBTypes } from '@openphone/dexie-database/types'

import type { PlanUpgradeAnalyticsData } from '@src/service/analytics/WorkspaceAnalyticsStore'
import type WorkflowDefinitionModel from '@src/service/model/workflow/WorkflowDefinitionModel'
import type { RawWorkflowStepDefinition } from '@src/service/model/workflow/WorkflowStepDefinitionModel'
import { computeBranchProperties } from '@src/service/utils/branchStepConfigUtils'

// eslint-disable-next-line import-x/no-restricted-paths
import { OdysseyAnalyticsBridge } from '@odyssey/analytics/OdysseyAnalyticsBridge'

type StepDefinitionId = DBTypes.WorkflowStepDefinitionRow['id']
type TriggerDefinitionId = DBTypes.WorkflowTriggerDefinitionRow['id']

type TriggerType = TriggerDefinitionId
type WorkflowType = 'call flow' | 'forward all calls'
export type BlockType = StepDefinitionId | TriggerDefinitionId
export type BlockCategory = RawWorkflowStepDefinition['category']

type ConditionalBranchType = 'phone_menu' | 'branch_step'

/**
 * The two step kinds whose branches can carry a custom name, mapped to the
 * analytics label reported as `conditional_branch_type`.
 */
const CONDITIONAL_BRANCH_TYPE_BY_STEP: Partial<
  Record<StepDefinitionId, ConditionalBranchType>
> = {
  WSDphoneMenu: 'phone_menu',
  WSDconditional: 'branch_step',
}

export default class WorkflowAnalyticsStore {
  private readonly analytics: typeof OdysseyAnalyticsBridge

  constructor() {
    this.analytics = OdysseyAnalyticsBridge
  }

  /**
   * Tracks when the workflow builder page is viewed.
   *
   * @param has_block_selected whether a block was pre-selected when the builder was viewed
   */
  workflowBuilderViewed(has_block_selected: boolean) {
    void this.analytics.track('workflow_builder_viewed', {
      has_block_selected,
    })
  }

  /**
   * Tracks when the back button in the workflow builder header is clicked.
   *
   * @param has_unsaved_changes whether the workflow had unsaved changes when the back button was clicked
   */
  workflowBuilderBackButtonClicked(has_unsaved_changes: boolean) {
    void this.analytics.track('workflow_builder_back_button_clicked', {
      has_unsaved_changes,
    })
  }

  /**
   * Tracks when the enable/disable toggle in the workflow builder header is clicked.
   *
   * @param enabled whether the workflow was enabled or disabled
   * @param workflow_trigger the trigger type of the workflow
   */
  workflowBuilderToggleClicked(enabled: boolean, workflow_trigger: TriggerType) {
    void this.analytics.track('workflow_builder_toggle_clicked', {
      enabled,
      workflow_trigger,
    })
  }

  /**
   * Tracks when the discard button in the workflow status banner is clicked.
   */
  workflowDiscardChangesButtonClicked() {
    void this.analytics.track('workflow_discard_changes_button_clicked')
  }

  /**
   * Tracks when the save button in the workflow status banner is clicked.
   */
  workflowSaveChangesButtonClicked() {
    void this.analytics.track('workflow_save_changes_button_clicked')
  }

  /**
   * Tracks when the publish button in the workflow status banner is clicked.
   */
  workflowPublishChangesButtonClicked() {
    void this.analytics.track('workflow_publish_changes_button_clicked')
  }

  /**
   * Tracks when the publish menu option in the workflow title card is clicked.
   */
  workflowPublishChangesMenuOptionClicked() {
    void this.analytics.track('workflow_publish_changes_menu_option_clicked')
  }

  /**
   * Tracks when the “Upgrade” button on a step is clicked.
   *
   * @param trigger the trigger that caused the Upgrade button to be clicked
   * @param source the location of the upgrade trigger
   */
  workflowStepUpgradeButtonClicked({ trigger, source }: PlanUpgradeAnalyticsData) {
    void this.analytics.track('workflow_step_upgrade_button_clicked', {
      trigger,
      source,
    })
  }

  /**
   * Tracks when the zoom level of the workflow canvas is changed.
   *
   * @param zoom_value the new zoom value. Can be a percentage or a keyword like 'fit', 'in', or 'out'
   * @param trigger how the zoom value was changed
   */
  workflowCanvasZoomChanged(zoom_value: string, trigger: 'canvas controls' | 'wheel') {
    void this.analytics.track('workflow_canvas_zoom_changed', {
      zoom_value,
      trigger,
    })
  }

  /**
   * Tracks when a block on the workflow canvas is clicked.
   *
   * @param block_type the type of block that was clicked
   * @param block_category the category of block that was clicked
   */
  workflowCanvasBlockClicked(block_type: BlockType, block_category: BlockCategory) {
    void this.analytics.track('workflow_canvas_block_clicked', {
      block_type,
      block_category,
    })
  }

  /**
   * Tracks when the “plus” button that appears on hover is clicked.
   */
  workflowCanvasAddBlockButtonClicked() {
    void this.analytics.track('workflow_canvas_add_block_button_clicked')
  }

  /**
   * Tracks when a block is added to the workflow canvas.
   *
   * @param block_type the type of block that was added
   * @param block_category the category of block that was added
   * @param source where the block was added from
   */
  workflowCanvasBlockAdded(
    block_type: BlockType,
    block_category: BlockCategory,
    source: 'add menu' | 'drag' | 'next steps',
  ) {
    void this.analytics.track('workflow_canvas_block_added', {
      block_type,
      block_category,
      source,
    })
  }

  /**
   * Tracks when a block is replaced via a drag/click and drop interaction.
   *
   * @param block_type the type of block that was added
   * @param block_category the category of block that was added
   * @param previous_block_type the type of block that was replaced
   * @param previous_block_category the category of block that was replaced
   */
  workflowCanvasBlockReplaced(
    block_type: BlockType,
    block_category: BlockCategory,
    previous_block_type: BlockType,
    previous_block_category: BlockCategory,
  ) {
    void this.analytics.track('workflow_canvas_block_replaced', {
      block_type,
      block_category,
      previous_block_type,
      previous_block_category,
    })
  }

  /**
   * Tracks when an option from a block’s context menu is selected.
   *
   * @param action the action that was selected from the block’s context menu
   * @param block_type the type of block for which the option was selected
   */
  workflowCanvasBlockMenuOptionSelected(
    action: 'edit' | 'replace' | 'delete',
    block_type: BlockType,
  ) {
    void this.analytics.track('workflow_canvas_block_menu_option_selected', {
      action,
      block_type,
    })
  }

  /**
   * Tracks when the users uses a keyboard or command shortcut
   *
   * @param source how the shortcut was triggered
   * @param action the action that was triggered
   */
  workflowCanvasShortcutPerformed(
    source: 'keyboard' | 'command palette',
    action:
      | 'delete block'
      | 'zoom in'
      | 'zoom out'
      | 'zoom to 100'
      | 'fit to view'
      | 'undo'
      | 'redo',
  ) {
    void this.analytics.track('workflow_canvas_shortcut_performed', {
      source,
      action,
    })
  }

  /**
   * Tracks when the user clicks the undo or redo button in the workflow canvas
   *
   * @param action the action that was triggered
   */
  workflowCanvasHistoryNavigated(action: 'undo' | 'redo') {
    void this.analytics.track('workflow_canvas_history_navigated', {
      action,
    })
  }

  /**
   * Tracks when a block from the workflow details view of the side panel is dragged.
   *
   * @param block_type the type of block that was dragged
   * @param block_category the category of block that was dragged
   */
  workflowSidePanelBlockDragged(block_type: BlockType, block_category: BlockCategory) {
    void this.analytics.track('workflow_side_panel_block_dragged', {
      block_type,
      block_category,
    })
  }

  /**
   * Tracks when the back button on the block details view of the side panel is clicked.
   *
   * @param view the side panel view that was opened when the back button was clicked
   */
  workflowSidePanelBackButtonClicked(
    view: 'block details' | 'replace block' | 'add next step',
  ) {
    void this.analytics.track('workflow_side_panel_back_button_clicked', {
      view,
    })
  }

  /**
   * Tracks when the replace button on the block details view of the side panel is clicked.
   *
   * @param block_type the type of block for which the replace button was clicked
   */
  workflowSidePanelReplaceBlockButtonClicked(block_type: BlockType) {
    void this.analytics.track('workflow_side_panel_replace_block_button_clicked', {
      block_type,
    })
  }

  /**
   * Tracks when the description of a workflow in the workflow details view of the side panel is updated.
   *
   * @param is_new whether the description is new. If false, that means the description was edited
   */
  workflowSidePanelDescriptionUpdated(is_new: boolean) {
    void this.analytics.track('workflow_side_panel_description_updated', {
      is_new,
    })
  }

  /**
   * Tracks when the delete button in the block details view of the side panel is clicked.
   *
   * @param block_type the type of block for which the delete button was clicked
   */
  workflowSidePanelDeleteBlockButtonClicked(block_type: BlockType) {
    void this.analytics.track('workflow_side_panel_delete_block_button_clicked', {
      block_type,
    })
  }

  /**
   * Tracks when one of the Next Steps in the block details side panel view is clicked.
   *
   * @param source_block_type the block that the side panel was opened for
   * @param target_branch_key the branch key of the clicked next step
   * @param target_block_type the type of the clicked next step. 'unset' if the user selects
   * the options to add a new step.
   */
  workflowSidePanelNextStepClicked(
    source_block_type: BlockType,
    target_branch_key: string,
    target_block_type: BlockType | 'unset',
  ) {
    void this.analytics.track('workflow_side_panel_next_step_clicked', {
      source_block_type,
      target_branch_key,
      target_block_type,
    })
  }

  /**
   * Tracks when one of the incoming call workflows is selected for viewing.
   *
   * @param source where the selection was made
   * @param workflow_type the type of workflow that was viewed
   */
  incomingCallWorkflowViewed(
    source: 'header' | 'side panel' | 'title card',
    workflow_type: WorkflowType,
  ) {
    void this.analytics.track('incoming_call_workflow_viewed', {
      source,
      workflow_type,
    })
  }

  /**
   * Tracks when a block is explicitly deleted.
   */
  workflowCanvasBlockDeleted() {
    void this.analytics.track('workflow_canvas_block_deleted')
  }

  /**
   * Tracks when a the AI Agent test call is clicked in FRI
   */
  onboardingTestAiAgentClicked(testingMethod: 'voice' | 'chat') {
    void this.analytics.track('onboarding_ai_agent_test_clicked', {
      testing_method: testingMethod,
    })
  }

  /**
   * Tracks when a new workflow is created.
   */
  workflowCreated() {
    void this.analytics.track('workflow_created')
  }

  /**
   * Tracks when a workflow is deleted.
   */
  workflowDeleted() {
    void this.analytics.track('workflow_deleted')
  }

  /**
   * Tracks when a workflow schedule is created.
   */
  workflowScheduleCreated(
    source:
      | 'workflow title card'
      | 'workflow status banner'
      | 'manage workflow schedules modal',
    page: 'phone number settings' | 'workflow builder',
  ) {
    void this.analytics.track('workflow_schedule_created', { source, page })
  }

  /**
   * Tracks when a workflow schedule is updated.
   */
  workflowScheduleUpdated(
    source: 'manage workflow schedules modal',
    page: 'phone number settings' | 'workflow builder',
  ) {
    void this.analytics.track('workflow_schedule_updated', { source, page })
  }

  /**
   * Tracks when a workflow schedule is cancelled.
   */
  workflowScheduleCancelled(
    source: 'manage workflow schedules modal',
    page: 'phone number settings' | 'workflow builder',
  ) {
    void this.analytics.track('workflow_schedule_cancelled', { source, page })
  }

  /**
   * Tracks when a workflow is successfully published (API call succeeded).
   * Captures the final branch step configuration at publish time.
   *
   * @param definition the workflow definition that was published
   */
  workflowPublished(definition: WorkflowDefinitionModel) {
    const branchProps = computeBranchProperties(definition.workflowSteps)
    void this.analytics.track('workflow_published', {
      workflow_definition_id: definition.id,
      ...branchProps,
    })
  }

  /**
   * Tracks when the is/is_not condition on a branch criteria is toggled by the user.
   *
   * @param workflow_definition_id the ID of the workflow definition being edited
   * @param filter_definition_id the filter definition ID (e.g. 'WFDknownContact')
   * @param negated whether the filter was set to negated (is_not) state
   */
  workflowBranchCriteriaUpdated({
    workflow_definition_id,
    filter_definition_id,
    negated,
  }: {
    workflow_definition_id: string
    filter_definition_id: string
    negated: boolean
  }) {
    void this.analytics.track('workflow_branch_criteria_updated', {
      workflow_definition_id,
      filter_definition_id,
      negated,
    })
  }

  /**
   * Tracks when a user commits a custom name on a conditional branch (a phone
   * menu option or a branch step), covering both naming an unnamed branch and
   * renaming an existing one.
   *
   * @param workflow_definition_id the ID of the workflow definition being edited
   * @param step_definition_id the step the branch belongs to; determines the
   *   reported `conditional_branch_type`
   */
  customBranchNameAdded({
    workflow_definition_id,
    step_definition_id,
  }: {
    workflow_definition_id: string
    step_definition_id: StepDefinitionId
  }) {
    const conditional_branch_type = CONDITIONAL_BRANCH_TYPE_BY_STEP[step_definition_id]
    if (!conditional_branch_type) {
      return
    }

    void this.analytics.track('custom_branch_name_added', {
      workflow_definition_id,
      conditional_branch_type,
    })
  }
}
